// Hook para gerenciar dados da apreciação
import { useState, useCallback } from 'react';
import { apiService } from '@/services/api';
import {
  criarChecklistPraxisPadrao,
  criarPraxisCamposDocumentoDefault,
  normalizarChecklistPraxis,
  normalizarPraxisCamposDocumento,
} from '@/types/praxis-document';
import {
  criarArCamposDocumentoDefault,
  normalizarArCamposDocumento,
} from '@/types/ar-document';

/**
 * Extrai data no formato YYYY-MM-DD sem viés de timezone.
 * Evita que datas "só dia" mudem de dia ao usar toISOString() em fusos negativos (ex.: Brasil).
 */
function extrairDataLocal(val: string | Date | null | undefined): string {
  if (val == null || val === '') return '';
  const str = typeof val === 'string' ? val.trim() : '';
  if (str && /^\d{4}-\d{2}-\d{2}/.test(str)) return str.substring(0, 10);
  const d = typeof val === 'object' && val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return '';
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

// Função auxiliar para converter URL relativa em URL completa
const construirUrlCompleta = (url: string | null | undefined): string | undefined => {
  if (!url) return undefined;

  // Se já é uma URL completa (começa com http:// ou https://), retornar como está
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Se é uma imagem base64 (data:image), retornar como está
  if (url.startsWith('data:image')) {
    return url;
  }

  // Se é uma URL relativa, construir a URL completa
  const envUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
  const baseUrl = envUrl.replace(/\/api$/, ''); // Remove /api do final

  // Se a URL já começa com /, apenas concatenar
  if (url.startsWith('/')) {
    return `${baseUrl}${url}`;
  }

  // Caso contrário, adicionar / antes
  return `${baseUrl}/${url}`;
};

export function useApreciacaoData() {
  const [apreciacao, setApreciacao] = useState<any>(null);
  const [dados, setDados] = useState<any>(null);
  const [riscos, setRiscos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const carregarApreciacao = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const response = await apiService.buscarApreciacao(id);
      if (response.success && response.data) {
        setApreciacao(response.data);
        const clienteTipo = response.data.projeto?.clienteTipo || 'arcelor';
        const clienteDefault = response.data.projeto?.nome || 'SENAI';
        const camposClienteNormalizados = clienteTipo === 'praxis'
          ? {
              ...(response.data.camposCliente || {}),
              praxis: normalizarPraxisCamposDocumento(response.data.camposCliente?.praxis, clienteDefault),
            }
          : clienteTipo === 'ar'
            ? {
                ...(response.data.camposCliente || {}),
                ar: normalizarArCamposDocumento(response.data.camposCliente?.ar, clienteDefault),
              }
            : (response.data.camposCliente || undefined);
        const checklistTecnicoAdminNormalizado = clienteTipo === 'praxis'
          ? normalizarChecklistPraxis(response.data.checklistTecnicoAdmin || criarChecklistPraxisPadrao())
          : (response.data.checklistTecnicoAdmin || undefined);

        // Preparar dados para edição
        setDados({
          numero: response.data.numero,
          numeroArcelorMittal: response.data.numeroArcelorMittal || '',
          revisao: response.data.revisao ?? 0,
          numeroEmitente: response.data.numeroEmitente || '',
          emitente: response.data.emitente || '',
          emitirCREA: response.data.emitirCREA || '',
          codigoInterno: response.data.codigoInterno || '',
          numeroDocumento: response.data.numeroDocumento || '',
          demanda: response.data.demanda || '',
          titulo: response.data.titulo || '',
          ativoId: response.data.ativoId || '',
          ativo: response.data.ativo || undefined,
          nomeMaquina: response.data.nomeMaquina || '',
          tipoMaquina: response.data.tipoMaquina || '',
          identificacaoMaquina: response.data.identificacaoMaquina || '',
          setorMaquina: response.data.setorMaquina || '',
          maquina: response.data.maquina ? {
            nome: response.data.maquina.nome || '',
            modelo: response.data.maquina.modelo || '',
            fabricante: response.data.maquina.fabricante || '',
            site: response.data.maquina.site || '',
            area: response.data.maquina.area || '',
            local: response.data.maquina.local || '',
            serie: response.data.maquina.serie || '',
            ano: response.data.maquina.ano || undefined,
            descricao: response.data.maquina.descricao || '',
          } : {
            nome: '',
            modelo: '',
            fabricante: '',
            site: '',
            area: '',
            local: '',
            serie: '',
            ano: undefined,
            descricao: '',
          },
          dataEmissao: extrairDataLocal(response.data.dataEmissao) || extrairDataLocal(new Date()),
          dataElaboracao: extrairDataLocal(response.data.dataElaboracao),
          dataVerificacao: extrairDataLocal(response.data.dataVerificacao),
          dataAprovacao: extrairDataLocal(response.data.dataAprovacao),
          statusLiberacao: response.data.statusLiberacao || 'nao_liberado',
          operadorEntrevistado: response.data.operadorEntrevistado || '',
          cargoOperador: response.data.cargoOperador || '',
          experiencia: response.data.experiencia || 0,
          turnosTrabalho: response.data.turnosTrabalho || '',
          frequenciaUso: response.data.frequenciaUso || '',
          modoOperacao: response.data.modoOperacao || '',
          historicoAcidentes: response.data.historicoAcidentes || '',
          limitesUso: response.data.limitesUso || '',
          limitesEspaco: response.data.limitesEspaco || '',
          limitesAdicionais: response.data.limitesAdicionais || '',
          limitesAmbientais: response.data.limitesAmbientais || '',
          limitacoes: response.data.limitacoes || '',
          mauUsoRazoavelmentePrevisivel: response.data.mauUsoRazoavelmentePrevisivel || [],
          normasTecnicasReferencia: response.data.normasTecnicasReferencia || [],
          nota1: response.data.nota1 || '',
          nota2: response.data.nota2 || '',
          observacoesEntrevista: response.data.observacoesEntrevista || '',
          dataEntrevista: extrairDataLocal(response.data.dataEntrevista),
          frequenciaAcessoOperacao: response.data.frequenciaAcessoOperacao || '',
          frequenciaAcessoManutencao: response.data.frequenciaAcessoManutencao || '',
          comprimentoHorizontal: response.data.comprimentoHorizontal || '',
          elevacao: response.data.elevacao || '',
          capacidadeCorreia: response.data.capacidadeCorreia || '',
          larguraCorreia: response.data.larguraCorreia || '',
          velocidadeCorreia: response.data.velocidadeCorreia || '',
          material: response.data.material || '',
          contraRecuo: response.data.contraRecuo || '',
          freio: response.data.freio || '',
          potencia: response.data.potencia || '',
          tensaoAcionamento: response.data.tensaoAcionamento || '',
          tensaoComando: response.data.tensaoComando || '',
          espacoPecasFerramentas: response.data.espacoPecasFerramentas ?? undefined,
          espacoOperacaoNormal: response.data.espacoOperacaoNormal ?? undefined,
          espacoManutencao: response.data.espacoManutencao ?? undefined,
          espacoAjuste: response.data.espacoAjuste ?? undefined,
          espacoLimpeza: response.data.espacoLimpeza ?? undefined,
          fotoPanoramicaUrl: construirUrlCompleta(response.data.fotoPanoramicaUrl),
          fotoIdentificacaoUrl: construirUrlCompleta(response.data.fotoIdentificacaoUrl),
          funcaoMaquina: response.data.funcaoMaquina || '',
          // Dados de usuários
          elaboradorUserId: response.data.elaboradorUserId || undefined,
          elaboradorExterno: response.data.elaboradorExterno || undefined,
          elaborador: response.data.elaborador || undefined,
          verificadorUserId: response.data.verificadorUserId || undefined,
          verificadorExterno: response.data.verificadorExterno || undefined,
          verificador: response.data.verificador || undefined,
          aprovadorUserId: response.data.aprovadorUserId || undefined,
          aprovadorExterno: response.data.aprovadorExterno || undefined,
          aprovador: response.data.aprovador || undefined,
          executanteUserId: response.data.executanteUserId || undefined,
          executanteExterno: response.data.executanteExterno || undefined,
          executante: response.data.executante || undefined,
          responsavelTecnicoUserId: response.data.responsavelTecnicoUserId || undefined,
          responsavelTecnicoExterno: response.data.responsavelTecnicoExterno || undefined,
          responsavelTecnico: response.data.responsavelTecnico || undefined,
          // Lista de verificação administrativa
          listaVerificacaoAdministrativa: response.data.listaVerificacaoAdministrativa || undefined,
          camposCliente: camposClienteNormalizados || (clienteTipo === 'praxis'
            ? { praxis: criarPraxisCamposDocumentoDefault(clienteDefault) }
            : clienteTipo === 'ar'
              ? { ar: criarArCamposDocumentoDefault(clienteDefault) }
              : undefined),
          checklistTecnicoAdmin: checklistTecnicoAdminNormalizado,
          conclusoesFinais: response.data.conclusoesFinais || '',
          exclusaoResponsabilidade: response.data.exclusaoResponsabilidade || '',
          cidadeAssinatura: response.data.cidadeAssinatura || '',
          clienteTipo,
          projetoId: response.data.projetoId,
          projeto: response.data.projeto,
        });

        // Mapear campos do backend para o formato do frontend
        // Backend retorna: probabilidadeFutura, frequenciaFutura, gravidadeFutura, exposicaoFutura
        // Frontend espera: probabilidadeFuturo, frequenciaFuturo, gravidadeFuturo, exposicaoFuturo
        let riscosMapeados = (response.data.riscos || []).map((risco: any) => ({
          ...risco,
          probabilidadeFuturo: risco.probabilidadeFutura,
          frequenciaFuturo: risco.frequenciaFutura,
          gravidadeFuturo: risco.gravidadeFutura,
          exposicaoFuturo: risco.exposicaoFutura,
        }));

        // Carregar imagens dos riscos ANTES de setRiscos, para que o preview/export PDF já tenha as fotos
        const imagensRes = await apiService.buscarRiscosImagens(id);
        if (imagensRes.success && imagensRes.data && Object.keys(imagensRes.data).length > 0) {
          riscosMapeados = riscosMapeados.map((r: any) => {
            const imgs = imagensRes.data![r.id];
            if (!imgs) return r;
            return {
              ...r,
              fotoAtualUrl1: imgs.fotoAtualUrl1 ?? r.fotoAtualUrl1,
              fotoAtualUrl2: imgs.fotoAtualUrl2 ?? r.fotoAtualUrl2,
              fotoAtualUrl3: imgs.fotoAtualUrl3 ?? r.fotoAtualUrl3,
              fotoAtualUrl4: imgs.fotoAtualUrl4 ?? r.fotoAtualUrl4,
              fotoPlrUrl: imgs.fotoPlrUrl ?? r.fotoPlrUrl,
            };
          });
        }

        setRiscos(riscosMapeados);

        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao carregar apreciação:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const limparDados = useCallback(() => {
    setApreciacao(null);
    setDados(null);
    setRiscos([]);
  }, []);

  return {
    apreciacao,
    dados,
    riscos,
    loading,
    setDados,
    setRiscos,
    carregarApreciacao,
    limparDados,
  };
}
